import { FullscreenOverlayContainer } from '@angular/cdk/overlay';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmService, EnvironmentService, HttpLoaderService, ListViewSelectionMode, PagedResult, SearchDataRequest, SearchModeEnum } from '@tymes4-shared';
import moment from 'moment';
import { BASE_BO_CONFIG } from '../../../../injectors';
import { BackofficeConfigModel, CustomerService, CustomerView, CustomerViewPagedResult } from '../../../api';
import { EditCustomerFormComponent } from '../../../dialogs/edit-customer-form/edit-customer-form.component';
import { AppUser } from '../../../models/app-user';
import { AuthService } from '../../../services/auth.service';
import { PersistedSearchService } from '../../../services/persisted-search.service';
import {CustomerBarcodeSearchDialogComponent} from "../customer-barcode-search-dialog/customer-barcode-search-dialog.component";
import { ApplicationModuleHelperService, ApplicationModules } from '../../../services/application-module.service';

interface RelationSearchFilter {
  query: string;
  customerType: number | null;
}

interface Customer {
  Addressline1: string;
  Channels: string;
  City: string;
  Country: string;
  CountryIso3: string;
  CustomerNumber: string;
  CustomerType: number;
  DateOfBirth: Date;
  Email: string;
  ExternalId: string;
  Id: number;
  Name: string;
  PostalCode: string;
  Street: string;
  StreetNr: string;
  TelephoneNr: string;
}

@Component({
    selector: 't4-customer-search-v2',
    templateUrl: 'customer-search-v2.component.html',
    styleUrls: ['customer-search-v2.component.scss'],
    standalone: false
})


export class CustomerSearchV2Component implements OnInit, AfterViewInit {

  public includeCompanies = true;
  public includeIndividuals = true;
  public searchMode: 'all' | 'any' = 'all';
  public showSearchTip = false;

  @ViewChild('listVw') listVw;
  @Output() onCustomerSelect = new EventEmitter<object>();

  @Input() viewMode: string = 'full'; //full | minimal | cash-register



  persistKey = 'customer-search-v2';
  loggedInUser?: AppUser;
  //Display options
  useFilter = false;
  allowEdit = true;
  loadDataOnOpen = false;
  showAddress = true;
  isMultiSelect = false;
  allowAdd = true;
  //End Display options

  selectedCustomers = null;
  isManagedFromExtSource = false;
  totalCustomerCount: number;
  searchText: string;
  appliedFilter: any = null;

  isIbaActive: boolean = false;
  isCustomerReworkActive: boolean = false;

  public listOptions = {
    filterRequired: true,
    search: false,
    showBarcodeSearch: true,
    pageSize: 12,
    selectionMode: ListViewSelectionMode.DirectSelect,
    fixedTableLayout: true,
    displayAIOptions: true,
    headers: [] as any[]
  };

  public isInArchiveMode = this.authService.isInArchiveMode();

  constructor(
    private customerService: CustomerService,
    private router: Router,
    private applicationModuleHelperService: ApplicationModuleHelperService,
    private confirmService: ConfirmService,
    private loader: HttpLoaderService,
    private dialog: MatDialog,
    private persistedSearchService: PersistedSearchService,
    private translate: TranslateService,
    private authService: AuthService,
    @Inject(BASE_BO_CONFIG) public config: BackofficeConfigModel,) {
  }

  ngOnInit() {
    this.loggedInUser = this.authService.getLoggedInUser();
    let pageSize = 12;


    //setup the listoptions etc
    switch (this.viewMode) {
      case 'full':
        break;

      case 'minimal':
        this.allowEdit = false;
        break;

      case 'cash-register':
        pageSize = 9;
        this.allowEdit = false;
        this.loadDataOnOpen = true;
        this.showAddress = false;
        this.isMultiSelect = true;
        this.allowAdd = true;
        break;
    }

    if (this.isMultiSelect === true) {
      (this.listOptions as any).selectionMode = ListViewSelectionMode.MultiSelect;
    }

    this.isManagedFromExtSource = this.config.companiesFromExternalSource;
    this.searchText = '';
    this.listOptions.pageSize = pageSize;

    this.listOptions.headers = this.buildHeaders();

    this.applicationModuleHelperService.getActiveModules().subscribe((activeModules) => {
      if (activeModules !== null) {
        this.isIbaActive = activeModules.some((module) => module.toUpperCase() == 'IBA')
        this.isCustomerReworkActive = activeModules.includes(ApplicationModules.CustomerRework);
      }

      this.listOptions.headers = this.buildHeaders();
      // The list view copies the headers when its options are bound; re-binding the options would also reset the sort.
      if (this.listVw) {
        this.listVw.listHeaders = this.listOptions.headers;
      }
    })

  }

  ngAfterViewInit() {
    setTimeout(() => {
      let persistedFilter = this.persistedSearchService.getPersistedSearch(this.persistKey);

      if (persistedFilter != null) {
        this.listOptions.filterRequired = true; //do not reload after the applied filter

        // Restore customer type filter if it exists
        if (persistedFilter.searchFilters && persistedFilter.searchFilters.hasOwnProperty('customerType')) {
          const customerType = persistedFilter.searchFilters.customerType;
          this.includeCompanies = customerType !== 2;
          this.includeIndividuals = customerType !== 1;
        }
        if (persistedFilter.searchMode) {
          this.searchMode = persistedFilter.searchMode === SearchModeEnum.Any ? 'any' : 'all';
        }

        this.listVw.reloadEarlierFilter(persistedFilter.searchFilters, persistedFilter.pageNr, persistedFilter.sortField, persistedFilter.sortAsc);
      } else {
        this.listOptions.filterRequired = !this.loadDataOnOpen;
      }
    });


  }

  private buildHeaders() {
    const headers: any[] = [
      {sortField: 'CustomerNumber', label: 'RELATIONS.OVERVIEW.LISTVIEW.HEADER.CUSTOMERNUMBER', width: 140},
      {sortField: 'FullName', label: 'RELATIONS.OVERVIEW.LISTVIEW.HEADER.NAME', isDefaultSort: 'asc', width: 200},
      {sortField: null, label: 'RELATIONS.OVERVIEW.LISTVIEW.HEADER.AGE', width: 140}
    ];

    if (this.isCustomerReworkActive) {
      headers.push({sortField: null, label: 'RELATIONS.OVERVIEW.LISTVIEW.HEADER.BELONGSTOCOMPANY', width: 200});
      headers.push({sortField: null, label: 'RELATIONS.OVERVIEW.LISTVIEW.HEADER.ISCOMPANY', width: 120});
    } else {
      headers.push({sortField: null, label: 'RELATIONS.OVERVIEW.LISTVIEW.HEADER.COMPANYNAME', width: 200});
    }

    headers.push({sortField: 'Email', label: 'RELATIONS.OVERVIEW.LISTVIEW.HEADER.EMAIL', width: 200});

    if (this.showAddress === true) {
      headers.push({sortField: null, label: 'RELATIONS.OVERVIEW.LISTVIEW.HEADER.ADDRESS', width: 200});
    }
    if (this.allowEdit === true) {
      headers.push({ sortField: null, label: '', width: 100});
    }

    return headers;
  }

  actionClick(action: 'add' | 'edit' | 'delete' | 'rowclick', customer: Customer = null) {

    switch (action) {
      case 'add':
        this.openDialog();
        break;
      case 'edit':
        this.openDialog(customer);
        break;
      case 'delete':
        this.openConfirmDialog(customer);
        break;
      case 'rowclick':

      if (this.viewMode !== 'full') {
        this.onCustomerSelect.emit( [ customer ]);
      }
      else {
        this.router.navigate(['relations/customers/details', customer.Id]);
      }

      break;
    }
  }

  onDataRequest(e: SearchDataRequest) {

    if (e.barcodeSearch === true) {
      this.customerPickupBarcode(e);
    }
    else {
      const searchFilters = {...e.searchFilters} as RelationSearchFilter;
      searchFilters.customerType = this.getCustomerTypeFilter();
      e.searchFilters = searchFilters; // Update the original searchFilters so it gets persisted
      e.searchMode = this.searchMode === 'all' ? SearchModeEnum.All : SearchModeEnum.Any;
      this.appliedFilter = searchFilters;
      this.updateFilter(e);
    }
  }

  fetchCustomers(sdr: SearchDataRequest) {

    this.persistedSearchService.setPersistedSearch(this.persistKey, sdr); //store for later

    this.loader.open();

    var additionalFilters = null;

    this.customerService.searchCustomersV2(this.searchText, sdr.pageNr, this.listOptions.pageSize, sdr.sortField, sdr.sortAsc, additionalFilters, null, sdr.searchMode).subscribe((resp: CustomerViewPagedResult) => {
      this.loader.close();
      this.listVw.Data = resp;
    });
  }

  OnItemSelectionChange($event){
      //tell the parent we are done
      this.selectedCustomers = $event;
      this.onCustomerSelect.emit(this.selectedCustomers);
  }


  setSelection(selection: Array<{Id: number}>) {
    this.selectedCustomers = selection;
    this.listVw.setSelection(selection);
  }

  updateFilter(sdr: SearchDataRequest) {

    sdr.searchFilters["customerType"] = this.getCustomerTypeFilter();

    if (this.appliedFilter !== void 0 && this.appliedFilter !== null) {
      this.useFilter = true;
      this.searchText = JSON.stringify(this.appliedFilter);
    } else {
      this.useFilter = false;
      this.searchText = sdr.searchText;
    }

    this.fetchCustomers(sdr);
  }

  onCustomerTypeChange(customerType: 'company' | 'individual') {
    if (!this.includeCompanies && !this.includeIndividuals) {
      if (customerType === 'company') {
        this.includeCompanies = true;
      } else {
        this.includeIndividuals = true;
      }
    }
  }

  private getCustomerTypeFilter(): number | null {
    if (!this.isCustomerReworkActive || (this.includeCompanies && this.includeIndividuals)) {
      return null;
    }

    return this.includeCompanies ? 1 : 2;
  }

  getCombinedAddress(customer) {

    if (customer == null) return '';
    return customer.Addressline1 + ', ' + customer.PostalCode + ' ' + customer.City;
  }


  openDialog(customer: Customer = null) {

    const isNew = customer === null;
    let title = isNew ? this.translate.instant('RELATIONS.EDITDIALOG.TITLE.NEW' ) : this.translate.instant('RELATIONS.EDITDIALOG.TITLE.EDIT') + ": " + customer.Name;
    let dialogRef: MatDialogRef<any> = this.dialog.open(EditCustomerFormComponent, {
      maxWidth: '100%',
      maxHeight: '100%',
      disableClose: true,
      data: { title: title, payload: customer ? customer.Id : customer, isNew: isNew }
    })
    dialogRef.afterClosed()
      .subscribe(id => {
          if (id) {
            this.listVw.refreshData();
          } else {
            return;
          }
        }
      )
  }

  openConfirmDialog(customer: Customer) {

    this.customerService.customerHasActiveSalesItems(customer.Id).subscribe(hasActiveSalesItems => {

      if (hasActiveSalesItems === true) {
        //not possible
        this.confirmService.confirm({
          title: this.translate.instant('GENERIC.CONFIRM.ERRORS.TITLE.DELETION-NOT-POSSIBLE'),
          message:  this.translate.instant('GENERIC.CONFIRM.ERRORS.MESSAGE.DELETION-NOT-POSSIBLE-IN-USE'),
          okonly: true
        }).subscribe((confirmed: boolean) => {
        });
      } else {
        this.confirmService.confirm({
          title: this.translate.instant('GENERIC.CONFIRM.WARNING.TITLE.ARE-YOU-SURE'),
          message: this.translate.instant('GENERIC.CONFIRM.WARNING.MESSAGE.CONFIRM-DELETE'),
          okonly: false
        }).subscribe((confirmed: boolean) => {
          this.loader.open();
          if (confirmed === true) {

            this.customerService.deleteCustomer(customer.Id).subscribe((data: any) => {
              //close this dialog and pass the newly added id to the called
              this.listVw.refreshData();
              this.loader.close();
            });
          } else {
            this.loader.close();
          }
        });
      }
    });
  }

  customerPickupBarcode(sdr: SearchDataRequest) {

    let dialogRef: MatDialogRef<any> = this.dialog.open(CustomerBarcodeSearchDialogComponent, {
      width: '500px',
      maxWidth: '98vw',
      disableClose: false,
      data: { }
    });
    dialogRef.afterClosed()
      .subscribe(customer => {
          if (customer) {

            sdr.searchFilters.CustomerNumber = customer.CustomerNumber;

            this.persistedSearchService.setPersistedSearch(this.persistKey, sdr); //store for later

            if (!this.isMultiSelect) {
              if (this.viewMode !== 'full') {
                this.onCustomerSelect.emit([customer]);
              } else {
                this.router.navigate(['relations/customers/details', customer.Id]);
              }
            } else {
              let check = true
              for (let x of this.selectedCustomers) {
                if (x.CustomerNumber == customer.CustomerNumber) {
                  check = false;
                }
              }
              if (check) {
                this.selectedCustomers.push(customer);
                this.listVw.setSelection(this.selectedCustomers);
              }
              const d : PagedResult =
              {
                TotalRecords: 1,
                Records: [customer],
                ResultCountCapped: false

              }

              this.listVw.Data = d;
            }

          }

        }
      );

  }

  showAge(age: number, customerType: number){
    if(customerType == 2){
      if(age){
        return age;
      }
      else{
        return this.translate.instant('RELATION.OVERVIEW.AGE.UNKNOWN');
      }
    }
    else{
      return ""
    }
  }

  constructCustomerIdentificationModel(item: CustomerView) {
    return {
      CalculatedName: item.Name,
      IsIdentified: item.IsIdentified,
      CustomerNumber: item.CustomerNumber,
      CustomerId: item.Id,
    }
  }
}
